/**
 * 根据给定的操作项构建用于生成展示文本的模板函数。
 *
 * 返回值为一个函数，该函数接受同样的 `operation` 对象并返回一个包含占位符的
 * 模板字符串。模板字符串的生成依据 `operation.operation`（操作类型）与
 * `operation.resourceType` / `operation.collectionType` / `operation.fatherResource`
 * 等字段来选择最合适的中文描述模板。
 *
 * 支持的占位符：
 * - {S} - 操作用户的名称（operation.userName）
 * - {O} - 目标资源名称（operation.resourceName）
 * - {OP} - 父资源名称（operation.fatherResource?.name）或 collection 名称（operation.collectionName）
 * - {OC} - collection 名称（operation.collectionName）
 *
 * 行为与回退：
 * - 若能匹配到更具体的资源类型/集合类型，会返回对应的具体模板；
 * - 在未匹配到具体模板时，会使用通用回退模板（例如：'在 {OP} 中创建了 {O}' 或类似模板）；
 * - 对于未识别的 `operation.operation` 类型，返回的函数会回退为返回 "operation.operation + ' {O}'" 的模板函数。
 *
 * 注意：本函数仅负责选择并返回模板字符串生成函数，不进行占位符替换。
 *
 * @param operation - 要用于选择模板的操作项（单个操作记录）
 * @returns 一个函数，接受相同的 `operation` 并返回带有占位符的中文模板字符串
 */

/**
 * 将单条 DomeOS 操作记录转换为可展示的中文描述文本。
 *
 * 此函数会：
 * 1. 调用 textMap 来获取对应操作的模板字符串（带占位符）；
 * 2. 用实际字段值替换模板中的占位符 {S}, {O}, {OP}, {OC}：
 *    - {S} => operation.userName
 *    - {O} => operation.resourceName
 *    - {OP} => operation.fatherResource?.name || operation.collectionName
 *    - {OC} => operation.collectionName
 * 3. 返回替换后的字符串；若在模板选择或替换过程中发生异常，则捕获并返回 null。
 *
 * 返回值为生成的字符串或 null（当发生错误或无法解析时）。
 *
 * @param operation - 单条操作记录，类型为 ApiGetDomeosOverviewOperationResponse[0]
 * @returns 替换占位符后的中文描述文本，出错时返回 null
 */
import type { ApiGetDomeosOverviewOperationResponse } from '@/api'

function textMap(operation: ApiGetDomeosOverviewOperationResponse[0]) {
  return (
    {
      SET: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          BASE_IMAGES: '添加了基础镜像 {O}',
          PROJECT_COLLECTION: '新建了项目 {O}',
          PROJECT: '向项目 {OP} 添加了工程 {O}',
          DEPLOY_COLLECTION: '新建了服务 {O}',
          DEPLOY: '向服务 {OP} 添加了部署 {O}',
          VERSION:
            {
              DEPLOY: '向服务 {OC} 的部署 {OP} 添加了版本 {O}',
              LOADBALANCER: '向负载均衡组 {OC} 的负载均衡 {OP} 添加了版本 {O}'
            }[(operation.fatherResource || {}).resourceType] ||
            '向 {OC} 的 {OP} 添加了版本 {O}',
          LOADBALANCER_COLLECTION: '新建了负载均衡组 {O}',
          LOADBALANCER: '向负载均衡组 {OP} 添加了负载均衡 {O}',
          CLUSTER: '添加了集群 {O}',
          BUNDLE: '在集群 {OP} 中分配了资源包 {O}',
          LB_RULES: '向负载均衡组 {OC} 的负载均衡 {OP} 中添加了一条规则',
          LB_TARGET: '向负载均衡组 {OC} 的负载均衡 {OP} 中添加了转发目标 {O}',
          ALARM_COLLECTION: '新建了报警组 {O}',
          ALARM_TEMPLATE: '向报警组 {OC} 添加了报警模板 {O}',
          CRONJOB: '向服务 {OP} 添加了定时任务 {O}',
          CRONVERSION: '向服务 {OC} 的定时任务 {OP} 添加了版本 {O}',
          BUNDLE_NODE: '向资源包 {OP} 添加了主机 {O}',
          CLUSTER_NODE: '向集群 {OP} 添加了主机 {O}',
          STORAGE_VOLUME: '为资源包 {OP} 关联了存储数据卷 {O}',
          LOG_CONFIG: '向资源包 {OP} 添加了日志收集配置 {O}',
          DBA_RULE: '向资源包 {OC} 添加了数据库授权规则 {O}',
          HPA_RULE: '在服务 {OC} 的部署 {OP} 下创建了自动伸缩配置 {O}'
        })[operation.resourceType] || '在 {OP} 中创建了 {O}',
      MODIFY: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          BASE_IMAGES: '修改了基础镜像 {O}',
          PROJECT_COLLECTION: '修改了项目 {O}',
          PROJECT: '修改了项目 {OP} 的工程 {O}',
          DEPLOY_COLLECTION: '修改了服务 {O}',
          DEPLOY: '修改了服务 {OP} 的部署 {O}',
          VERSION:
            {
              DEPLOY: '修改了服务 {OC} 下部署 {OP} 的版本 {O}',
              LOADBALANCER: '修改了负载均衡组 {OC} 下负载均衡 {OP} 的版本 {O}'
            }[(operation.fatherResource || {}).resourceType] ||
            '修改了 {OC} 下 {OP} 的版本 {O}',
          LOADBALANCER_COLLECTION: '修改了负载均衡组 {O}',
          LOADBALANCER: '修改了负载均衡组 {OP} 的负载均衡 {O}',
          CLUSTER: '修改了集群 {O}',
          BUNDLE: '修改了资源包 {O} 的配置',
          LB_RULES: '修改了负载均衡组 {OC} 下负载均衡 {OP} 的一条规则',
          LB_TARGET: '修改了负载均衡组 {OC} 下负载均衡 {OP} 的转发目标 {O}',
          ALARM_COLLECTION: '修改了报警组 {O}',
          ALARM_TEMPLATE: '修改了报警组 {OC} 的报警模板 {O}',
          CRONJOB: '修改了服务 {OP} 的定时任务 {O}',
          CRONVERSION: '修改了服务 {OC} 下定时任务 {OP} 的版本 {O}',
          BUNDLE_NODE: '修改了资源包 {OP} 中主机 {O} 的标签',
          STORAGE_VOLUME: '修改了资源包 {OP} 中的存储数据卷 {O}',
          LOG_CONFIG: '修改了资源包 {OP} 中的日志收集配置 {O}',
          DBA_RULE: '修改了资源包 {OC} 中的数据库授权规则 {O}',
          HPA_RULE: '修改了服务 {OC} 的部署 {OP} 下的自动伸缩配置 {O}'
        })[operation.resourceType] || '修改了 {OP} 中的 {O}',
      DELETE: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          BASE_IMAGES: '删除了基础镜像 {O}',
          PROJECT_COLLECTION: '删除了项目 {O}',
          PROJECT: '删除了项目 {OP} 的工程 {O}',
          DEPLOY_COLLECTION: '删除了服务 {O}',
          DEPLOY: '删除了服务 {OP} 的部署 {O}',
          LOADBALANCER_COLLECTION: '删除了负载均衡组 {O}',
          LOADBALANCER: '删除了负载均衡组 {OP} 的负载均衡 {O}',
          CLUSTER: '删除了集群 {O}',
          BUNDLE: '删除了集群 {OP} 的资源包 {O}',
          LB_RULES: '删除了负载均衡组 {OC} 下负载均衡 {OP} 的一条规则',
          LB_TARGET: '删除了负载均衡组 {OC} 下负载均衡 {OP} 的转发目标 {O}',
          ALARM_COLLECTION: '删除了报警组 {O}',
          ALARM_TEMPLATE: '删除了报警组 {OC} 的报警模板 {O}',
          ALARM_NOTIFICATION: '删除了报警组 {OC} 的事件报警 {O}',
          CRONJOB: '删除了服务 {OP} 的定时任务 {O}',
          CRONVERSION: '删除了服务 {OC} 定时任务 {OP} 的版本 {O}',
          BUNDLE_NODE: '从资源包 {OP} 中移出了主机 {O}',
          CLUSTER_NODE: '从集群 {OP} 中移除了主机 {O}',
          STORAGE_VOLUME: '资源包 {OP} 解除关联了存储数据卷 {O}',
          LOG_CONFIG: '删除了资源包 {OP} 的日志收集配置 {O}',
          DBA_RULE: '删除了资源包 {OC} 中的数据库授权规则 {O}',
          HPA_RULE: '删除了服务 {OC} 的部署 {OP} 下的自动伸缩配置 {O}'
        })[operation.resourceType] || '删除了 {OP} 中的 {O}',
      SYNC: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '向项目 {OP} 同步了镜像 {O}',
      TRIAL: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '试用了项目 {OP} 的镜像 {O}',
      BUILD: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '构建了 {OC} 中 {OP} 的镜像 {O}',
      AUTO_BUILD: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '自动构建了 {OC} 中 {OP} 的镜像 {O}',
      EXEC: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '执行了 {OC} 中 {OP} 的流水线 {O}',
      AUTO_EXEC: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '自动执行了 {OC} 中 {OP} 的流水线 {O}',
      ADDGROUPMEMBER: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          DEPLOY_COLLECTION: '向服务 {OC} 的用户组添加了成员 {O}',
          PROJECT_COLLECTION: '向项目 {OC} 的用户组添加了成员 {O}',
          ALARM_COLLECTION: '向报警 {OC} 的用户组添加了成员 {O}',
          LOADBALANCER_COLLECTION: '向负载均衡 {OC} 的用户组添加了成员 {O}',
          BUNDLE: '向资源包 {OC} 的用户组添加了成员 {O}',
          CLUSTER: '向集群 {OC} 的用户组添加了成员 {O}'
        })[operation.collectionType] || '向 {OC} 的用户组添加了成员 {O}',
      MODIFYGROUPMEMBER: (
        operation: ApiGetDomeosOverviewOperationResponse[0]
      ) =>
        ({
          DEPLOY_COLLECTION: '修改了服务 {OC} 的用户组中 {O} 的权限',
          PROJECT_COLLECTION: '修改了项目 {OC} 的用户组中 {O} 的权限',
          ALARM_COLLECTION: '修改了报警 {OC} 的用户组中 {O} 的权限',
          LOADBALANCER_COLLECTION: '修改了负载均衡 {OC} 的用户组中 {O} 的权限',
          BUNDLE: '修改了资源包 {OC} 的用户组中 {O} 的权限',
          CLUSTER: '修改了集群 {OC} 的用户组中 {O} 的权限'
        })[operation.collectionType] || '修改了 {OC} 的用户组中 {O} 的权限',
      DELETEGROUPMEMBER: (
        operation: ApiGetDomeosOverviewOperationResponse[0]
      ) =>
        ({
          DEPLOY_COLLECTION: '从服务 {OC} 的用户组中删除了 {O}',
          PROJECT_COLLECTION: '从项目 {OC} 的用户组中删除了 {O}',
          ALARM_COLLECTION: '从报警 {OC} 的用户组中删除了 {O}',
          LOADBALANCER_COLLECTION: '从负载均衡 {OC} 的用户组中删除了 {O}',
          BUNDLE: '从资源包 {OC} 的用户组中删除了 {O}',
          CLUSTER: '从集群 {OC} 的用户组中删除了 {O}'
        })[operation.collectionType] || '从 {OC} 的用户组中删除了 {O}',
      START: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          DEPLOY: '启动了服务 {OP} 的部署 {O}',
          LOADBALANCER: '启动了负载均衡组 {OP} 的负载均衡 {O}',
          CRONJOB: '启动了服务 {OP} 的定时任务 {O}',
          SYNC: '启动了项目 {OP} 中镜像 {O} 的镜像同步'
        })[operation.resourceType] || '启动了 {OP} 的 {O}',
      STOP: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          DEPLOY: '停止了服务 {OP} 的部署 {O}',
          LOADBALANCER: '停止了负载均衡组 {OP} 的负载均衡 {O}',
          CRONJOB: '停止了服务 {OP} 的定时任务 {O}',
          SYNC: '停止了项目 {OP} 中镜像 {O} 的镜像同步'
        })[operation.resourceType] || '停止了 {OP} 的 {O}',
      UPDATE: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          DEPLOY: '升级了服务 {OP} 的部署 {O}',
          LOADBALANCER: '升级了负载均衡组 {OP} 的负载均衡 {O}',
          CRONJOB: '升级了服务 {OP} 的定时任务 {O}'
        })[operation.resourceType] || '升级了 {OP} 的 {O}',
      AUTO_DEPLOY: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          DEPLOY: '自动升级了服务 {OP} 的部署 {O}',
          LOADBALANCER: '自动升级了负载均衡组 {OP} 的负载均衡 {O}'
        })[operation.resourceType] || '升级了 {OP} 的 {O}',
      ABORT: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          DEPLOY: '中止了对服务 {OP} 的部署 {O} 的操作',
          LOADBALANCER: '中止了对负载均衡组 {OP} 的负载均衡 {O} 的操作'
        })[operation.resourceType] || '升级了 {OP} 的 {O}',
      RESTARTINSTANCE: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          DEPLOY: '重新启动了服务 {OP} 的部署 {O} 的实例',
          LOADBALANCER: '重新启动了负载均衡组 {OP} 的负载均衡 {O} 的实例'
        })[operation.resourceType] || '重新启动了 {OP} 的 {O} 的实例',
      OPENUPDOWNFILEENTRANCE: (
        operation: ApiGetDomeosOverviewOperationResponse[0]
      ) =>
        ({
          DEPLOY: '启动了服务 {OP} 的部署 {O} 的文件管理入口',
          LOADBALANCER: '启动了负载均衡组 {OP} 的负载均衡 {O} 的文件管理入口',
          CRONJOB: '启动了服务 {OP} 的定时任务 {O} 的文件管理入口'
        })[operation.resourceType] || '升级了 {OP} 的 {O}',
      DELETELOADBALANCER: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '删除了服务 {OP} 的部署 {O} 的网络服务',
      UPDATELOADBALANCER: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '修改了服务 {OP} 的部署 {O} 的网络服务',
      ADDLOADBALANCER: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '向服务 {OP} 的部署 {O} 添加了网络服务',
      DEPRECATE: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          DEPLOY: '废弃了服务 {OC} 的部署 {OP} 的版本 {O}',
          LOADBALANCER: '废弃了负载均衡组 {OC} 的负载均衡 {OP} 的版本 {O}'
        })[(operation.fatherResource || {}).resourceType] ||
        '废弃了 {OC} 的 {OP} 的版本 {O}',
      ENABLE: (operation: ApiGetDomeosOverviewOperationResponse[0]) =>
        ({
          DEPLOY: '恢复启用了服务 {OC} 的部署 {OP} 的版本 {O}',
          LOADBALANCER: '恢复启用了负载均衡组 {OC} 的负载均衡 {OP} 的版本 {O}'
        })[(operation.fatherResource || {}).resourceType] ||
        '恢复启用了 {OC} 的 {OP} 的版本 {O}',
      SILENCE: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '抑制了报警组 {OC} 中关于 {O} 的报警',
      RECOVER: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '取消抑制了报警组 {OC} 中关于 {O} 的报警',
      DELETE_JOBHISTORY: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '删除了定时任务 {O} 的历史作业记录',
      DRAIN_NODE: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '停用了资源包 {OP} 中的主机 {O}',
      RECOVER_NODE: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '取消停用了资源包 {OP} 中的主机 {O}',
      CORDON_NODE: (_: ApiGetDomeosOverviewOperationResponse[0]) =>
        '设置了资源包 {OP} 中的主机 {O} 为不可调度'
    }[operation.operation] ||
    ((operation: ApiGetDomeosOverviewOperationResponse[0]) =>
      operation.operation + ' {O}')
  )
}

function domeosOperation2Text(
  operation: ApiGetDomeosOverviewOperationResponse[0]
): string | null {
  try {
    const t = textMap(operation)(operation)
    const m = {
      S: operation.userName,
      O: operation.resourceName,
      OP: operation.fatherResource?.name || operation?.collectionName,
      OC: operation.collectionName
    }
    return t.replace(
      /{(S|O|OP|OC)}/g,
      (_: unknown, key: keyof typeof m) => m[key] || ''
    )
  } catch (e) {
    return null
  }
}

export { domeosOperation2Text }
